/**
 * Cliente HTTP do console de administração (E53.F5–F7, ADR-0064). // ux-neutrality:allow doc-comment
 *
 * Espelha os contratos do servidor sem recalcular nada: quem decide o que é // ux-neutrality:allow doc-comment
 * legítimo (papel no ambiente, último gestor, vínculo com casos) é o servidor, e // ux-neutrality:allow doc-comment
 * a tela apenas mostra a razão da recusa. Cada falha vira um `AdminError.reason` // ux-neutrality:allow doc-comment
 * estável — a mensagem em pt-BR mora no catálogo, não aqui. // ux-neutrality:allow doc-comment
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';

export type AmbienteAdmin = 'plataforma' | 'escritorio' | 'banco';
export type EstadoAdmin = 'ativos' | 'inativos' | 'todos';
export type PapelAdmin =
  | 'owner'
  | 'member'
  | 'revisor'
  | 'certificador'
  | 'bank_manager'
  | 'bank_user';

export interface EscritorioAdmin {
  id: string;
  nome: string;
  cnpj: string | null;
  ambiente: AmbienteAdmin;
  /**
   * `banco_id`/`banco_nome` só vêm preenchidos com EXATAMENTE um vínculo. // ux-neutrality:allow doc-comment
   *
   * O vínculo é N:N desde a E57.F1: com dois ou mais, o servidor manda os dois // ux-neutrality:allow doc-comment
   * nulos porque nenhum deles cabe numa célula, e quem responde "quantos são" é // ux-neutrality:allow doc-comment
   * `total_bancos`. Ler só `banco_nome` faz a tela dizer "sem vínculo" para um // ux-neutrality:allow doc-comment
   * escritório que atende dois clientes (E65.F2.T02). // ux-neutrality:allow doc-comment
   */
  banco_id: string | null;
  banco_nome: string | null;
  total_bancos: number;
  is_platform: boolean;
  ativo: boolean;
  total_usuarios: number;
  /** `null` = fora do alcance de leitura da plataforma (≠ "nenhum caso"). */
  total_casos: number | null;
  criado_em: string;
}

export interface UsuarioAdmin {
  id: string;
  email: string;
  display_name: string | null;
  role: PapelAdmin;
  ambiente: AmbienteAdmin;
  escritorio_id: string | null;
  escritorio_nome: string | null;
  banco_id: string | null;
  banco_nome: string | null;
  ativo: boolean;
  criado_em: string;
}

export interface PaginaAdmin<T> {
  itens: T[];
  total: number;
  limite: number;
  offset: number;
}

export interface ClienteAdmin {
  id: string;
  nome: string;
  cnpj: string | null;
  total_escritorios: number;
  criado_em: string;
}

export type FonteTrilha =
  | 'administracao'
  | 'assinatura'
  | 'medicao'
  | 'requisicao'
  | 'edicao_laudo'
  | 'compartilhamento_laudo';

export interface TrilhaEvento {
  id: string;
  fonte: FonteTrilha;
  acao: string;
  ocorrido_em: string;
  /** `null` quando o fato não tem autor humano (a medição nasce da conclusão do serviço). */
  autor_email: string | null;
  ambiente: AmbienteAdmin;
  objeto_tipo: string;
  objeto_id: string;
  objeto_rotulo: string | null;
  detalhe: Record<string, unknown>;
}

export interface FiltroTrilha {
  desde?: string;
  ate?: string;
  ambiente?: AmbienteAdmin;
  autor?: string;
  fonte?: FonteTrilha;
  limite?: number;
  offset?: number;
}

export interface FiltroAdmin {
  busca?: string;
  ambiente?: AmbienteAdmin;
  estado?: EstadoAdmin;
  limite?: number;
  offset?: number;
}

export type AdminErrorReason =
  | 'conflict'
  | 'validation'
  | 'not_found'
  | 'unauthorized'
  | 'network'
  | 'unknown'
  | 'escritorio_com_casos'
  | 'desvinculo_ambiguo'
  | 'plataforma_nao_tem_cliente'
  | 'ultima_plataforma'
  | 'acao_sobre_si_mesmo'
  | 'ultimo_gestor'
  | 'papel_fora_do_ambiente'
  | 'email_ja_cadastrado'
  | 'export_muito_grande'
  | 'plataforma_nao_encerra'
  | 'escritorio_encerrado';

export class AdminError extends Error {
  public readonly reason: AdminErrorReason;

  constructor(reason: AdminErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'AdminError';
  }
}

/**
 * Código do servidor → motivo estável da tela. // ux-neutrality:allow doc-comment
 *
 * O código é o contrato; a frase é do catálogo. Sem este mapa, a tela cairia no // ux-neutrality:allow doc-comment
 * genérico "não foi possível concluir" justamente quando o servidor explicou o // ux-neutrality:allow doc-comment
 * porquê. // ux-neutrality:allow doc-comment
 */
const _DETALHE_PARA_MOTIVO: Record<string, AdminErrorReason> = {
  ESCRITORIO_COM_CASOS_DO_CLIENTE: 'escritorio_com_casos',
  // E65.F2.T02: o servidor emite este código desde a E57.F1 e o catálogo nunca
  // o teve — a recusa caía no genérico "não foi possível concluir" justamente
  // quando havia uma razão exata para dar. Catálogo sem paridade com os
  // produtores do erro é mensagem opaca com aparência de tratamento.
  DESVINCULO_AMBIGUO: 'desvinculo_ambiguo',
  PLATAFORMA_NAO_TEM_CLIENTE: 'plataforma_nao_tem_cliente',
  ULTIMA_PLATAFORMA: 'ultima_plataforma',
  // E69.F1 (ADR-0072): o escritório-plataforma não encerra; pessoa de
  // escritório encerrado não é reativada avulsa.
  PLATAFORMA_NAO_ENCERRA: 'plataforma_nao_encerra',
  ESCRITORIO_ENCERRADO: 'escritorio_encerrado',
  ACAO_SOBRE_SI_MESMO: 'acao_sobre_si_mesmo',
  ULTIMO_GESTOR_DO_AMBIENTE: 'ultimo_gestor',
  PAPEL_FORA_DO_AMBIENTE: 'papel_fora_do_ambiente',
  EMAIL_JA_CADASTRADO: 'email_ja_cadastrado',
  TRILHA_EXPORT_MUITO_GRANDE: 'export_muito_grande',
  PLATFORM_REQUIRED: 'unauthorized',
  OWNER_REQUIRED: 'unauthorized',
  ESTRUTURA_NAO_ENCONTRADA: 'not_found',
  PESSOA_NAO_ENCONTRADA: 'not_found',
};

const _STATUS_PARA_MOTIVO: Record<number, AdminErrorReason> = {
  401: 'unauthorized',
  403: 'unauthorized',
  404: 'not_found',
  409: 'conflict',
  422: 'validation',
};

function _motivo(err: AxiosError): AdminErrorReason {
  const detalhe = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detalhe === 'string') {
    const porCodigo = _DETALHE_PARA_MOTIVO[detalhe];
    if (porCodigo !== undefined) return porCodigo;
  }
  const status = err.response?.status;
  if (status !== undefined) {
    const porStatus = _STATUS_PARA_MOTIVO[status];
    if (porStatus !== undefined) return porStatus;
  }
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return 'network';
  return 'unknown';
}

function _classificar(err: unknown): AdminError {
  if (!axios.isAxiosError(err)) return new AdminError('unknown');
  return new AdminError(_motivo(err as AxiosError));
}

function _params(filtro: FiltroAdmin): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (filtro.busca !== undefined && filtro.busca.trim() !== '') params['busca'] = filtro.busca;
  if (filtro.ambiente !== undefined) params['ambiente'] = filtro.ambiente;
  if (filtro.estado !== undefined) params['estado'] = filtro.estado;
  if (filtro.limite !== undefined) params['limite'] = filtro.limite;
  if (filtro.offset !== undefined) params['offset'] = filtro.offset;
  return params;
}

export async function listEscritorios(
  client: AxiosInstance,
  filtro: FiltroAdmin = {},
): Promise<PaginaAdmin<EscritorioAdmin>> {
  try {
    const { data } = await client.get<PaginaAdmin<EscritorioAdmin>>('/plataforma/escritorios', {
      params: _params(filtro),
    });
    return data;
  } catch (err) {
    throw _classificar(err);
  }
}

export async function listUsuarios(
  client: AxiosInstance,
  filtro: FiltroAdmin = {},
): Promise<PaginaAdmin<UsuarioAdmin>> {
  try {
    const { data } = await client.get<PaginaAdmin<UsuarioAdmin>>('/plataforma/usuarios', {
      params: _params(filtro),
    });
    return data;
  } catch (err) {
    throw _classificar(err);
  }
}

export async function listClientes(client: AxiosInstance): Promise<ClienteAdmin[]> {
  try {
    const { data } = await client.get<ClienteAdmin[]>('/plataforma/clientes');
    return data;
  } catch (err) {
    throw _classificar(err);
  }
}

export async function criarCliente(
  client: AxiosInstance,
  payload: { nome: string; cnpj?: string | null },
): Promise<ClienteAdmin> {
  try {
    const { data } = await client.post<ClienteAdmin>('/plataforma/clientes', {
      nome: payload.nome,
      cnpj: payload.cnpj ?? null,
    });
    return data;
  } catch (err) {
    throw _classificar(err);
  }
}

/**
 * Cria um escritório pelo console, com o primeiro gestor no mesmo passo (E65.F5). // ux-neutrality:allow doc-comment
 *
 * A senha é provisória por contrato (a troca é obrigatória no primeiro acesso) e // ux-neutrality:allow doc-comment
 * o vínculo com o cliente é opcional — as duas decisões estão explícitas no nome // ux-neutrality:allow doc-comment
 * dos campos porque são regra do servidor, não conveniência da tela. // ux-neutrality:allow doc-comment
 */
export async function criarEscritorio(
  client: AxiosInstance,
  payload: {
    nome: string;
    cnpj?: string | null;
    gestor_email: string;
    gestor_senha_provisoria: string;
    gestor_nome?: string | null;
    cliente_id?: string | null;
  },
): Promise<{ id: string; nome: string }> {
  try {
    const { data } = await client.post<{ id: string; nome: string }>('/plataforma/escritorios', {
      nome: payload.nome,
      cnpj: payload.cnpj ?? null,
      gestor_email: payload.gestor_email,
      gestor_senha_provisoria: payload.gestor_senha_provisoria,
      gestor_nome: payload.gestor_nome ?? null,
      cliente_id: payload.cliente_id ?? null,
    });
    return data;
  } catch (err) {
    throw _classificar(err);
  }
}

export async function definirVinculo(
  client: AxiosInstance,
  escritorioId: string,
  bancoId: string | null,
): Promise<void> {
  try {
    await client.patch(`/plataforma/escritorios/${escritorioId}/vinculo`, { banco_id: bancoId });
  } catch (err) {
    throw _classificar(err);
  }
}

export async function definirPlataforma(
  client: AxiosInstance,
  escritorioId: string,
  isPlatform: boolean,
): Promise<void> {
  try {
    await client.patch(`/plataforma/escritorios/${escritorioId}/plataforma`, {
      is_platform: isPlatform,
    });
  } catch (err) {
    throw _classificar(err);
  }
}

/** Encerra (`true`) ou reabre (`false`) o escritório — encerrar desativa todas as pessoas. */
export async function definirEncerramento(
  client: AxiosInstance,
  escritorioId: string,
  encerrado: boolean,
): Promise<void> {
  try {
    await client.patch(`/plataforma/escritorios/${escritorioId}/encerramento`, { encerrado });
  } catch (err) {
    throw _classificar(err);
  }
}

export async function criarPessoa(
  client: AxiosInstance,
  payload: {
    email: string;
    senha_provisoria: string;
    role: PapelAdmin;
    display_name?: string | null;
    escritorio_id?: string | null;
    banco_id?: string | null;
  },
): Promise<UsuarioAdmin> {
  try {
    const { data } = await client.post<UsuarioAdmin>('/plataforma/usuarios', payload);
    return data;
  } catch (err) {
    throw _classificar(err);
  }
}

export async function trocarPapel(
  client: AxiosInstance,
  usuarioId: string,
  role: PapelAdmin,
): Promise<void> {
  try {
    await client.patch(`/plataforma/usuarios/${usuarioId}/papel`, { role });
  } catch (err) {
    throw _classificar(err);
  }
}

export async function definirAcesso(
  client: AxiosInstance,
  usuarioId: string,
  ativo: boolean,
): Promise<void> {
  try {
    await client.patch(`/plataforma/usuarios/${usuarioId}/acesso`, { ativo });
  } catch (err) {
    throw _classificar(err);
  }
}

/**
 * Recorte da trilha → query string, omitindo o que está vazio. // ux-neutrality:allow doc-comment
 *
 * Mandar `autor=''` faria o servidor recortar por uma busca vazia em vez de não // ux-neutrality:allow doc-comment
 * recortar — daí a limpeza antes de montar. // ux-neutrality:allow doc-comment
 */
function _paramsTrilha(filtro: FiltroTrilha): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const [chave, valor] of Object.entries(filtro)) {
    if (valor === undefined || valor === null) continue;
    if (typeof valor === 'string' && valor.trim() === '') continue;
    params[chave] = valor as string | number;
  }
  return params;
}

export async function listTrilha(
  client: AxiosInstance,
  filtro: FiltroTrilha = {},
): Promise<PaginaAdmin<TrilhaEvento>> {
  try {
    const { data } = await client.get<PaginaAdmin<TrilhaEvento>>('/plataforma/trilha', {
      params: _paramsTrilha(filtro),
    });
    return data;
  } catch (err) {
    throw _classificar(err);
  }
}

/** Baixa o CSV do MESMO recorte da consulta (o servidor recusa export grande demais). */
export async function exportarTrilha(
  client: AxiosInstance,
  filtro: FiltroTrilha = {},
): Promise<Blob> {
  try {
    const { data } = await client.get<Blob>('/plataforma/trilha/export.csv', {
      params: _paramsTrilha(filtro),
      responseType: 'blob',
    });
    return data;
  } catch (err) {
    throw _classificar(err);
  }
}

/** Data ISO do servidor → data curta pt-BR (sem hora). */
export function formatarData(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  return data.toLocaleDateString('pt-BR');
}

// `formatarDataHora` e `instanteDoDiaLocal` saíram daqui na E61.F4.T01, para
// `lib/trilha_format.ts`: as duas telas da trilha precisam delas, e o módulo do
// banco não pode importar do cliente HTTP de `/plataforma/*`.
